import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';

import { isDirectAction, selectActiveAction, selectIsAnyToolActionActive, selectNeedCancelConfirmation } from 'store/selectors/toolSelector';
import { getActionHandler, getToolModeHandlers } from '../ToolRegistry';
import { KEY, KEY_TO_ACTION } from './KeyBindings';
import { disableAction, enableAction } from './ActionHandlers';

export default function KeyboardShortcutsWarningModalRoof() {
	const dispatch = useDispatch<AppDispatch>();

	const activeAction = useSelector(selectActiveAction);
	// Single source of truth for "something else is active" - covers Map Only too, so no
	// shortcut can start a new mode while it's on.
	const isAnyToolActionActive = useSelector(selectIsAnyToolActionActive);
	const needConfirmationOnCancel = useSelector(selectNeedCancelConfirmation);
	const selectedRoofIndex = useSelector((s: RootState)=>s.toolNewPostions.data.selectedRoofIndex);

	const [showConfirm, setShowConfirm] = useState(false);
	const handlersRef = useRef<any>(null);
	const pendingKeyRef = useRef<KEY | null>(null);

	// Load tool handlers when action changes
	useEffect(() => {
		if (activeAction) {
			handlersRef.current = getToolModeHandlers(activeAction);
		}
	}, [activeAction]);

	const confirmCancel = () => {
		handlersRef.current?.cancel?.();
		setShowConfirm(false);

		const key = pendingKeyRef.current;
		pendingKeyRef.current = null;
		if (!key) return;

		const action = KEY_TO_ACTION[key];
		if (!action) return;

		enableAction(dispatch, action);
	};

	const resetAll = useCallback(() => {
		console.log('Resetting all tool modes due to keyboard shortcut', needConfirmationOnCancel);
		if (needConfirmationOnCancel) {
			setShowConfirm(true);
			return;
		}
		if (activeAction) {
			console.log(activeAction);
			disableAction(dispatch, activeAction);
		}
	}, [needConfirmationOnCancel, activeAction, dispatch]);

	useEffect(() => {
		const handleKeyUp = (event: KeyboardEvent) => {
			const key = event.key as KEY;
			if (!Object.values(KEY).includes(key)) return;

			const action = KEY_TO_ACTION[key];
			if (!action || activeAction || isAnyToolActionActive) return;

			if (action === activeAction) {
				return;
			}

			//If cancel confirmation is needed for active mode
			if (needConfirmationOnCancel) {
				pendingKeyRef.current = key;
				setShowConfirm(true);
				return;
			}

			// Skip if the currently active action is a direct action
			// (need to hnadle if any without confirmation)
			const isAcitveActionIsDirectAction = activeAction && isDirectAction(activeAction);
			if (isAcitveActionIsDirectAction) return;

			resetAll();

			//If the key corresponds to a direct action, call its registered handler
			if (isDirectAction(action)) {
				getActionHandler(action)?.();
				return;
			}

			// enable the mode
			enableAction(dispatch, action);
		};

		window.addEventListener('keyup', handleKeyUp);
		return () => window.removeEventListener('keyup', handleKeyUp);
	}, [needConfirmationOnCancel, resetAll, dispatch, activeAction, isAnyToolActionActive, selectedRoofIndex]);

	return (
		<ConfirmationModal
			open={showConfirm && needConfirmationOnCancel}
			message={handlersRef.current?.cancelConfirmMessage || 'Are you sure you want to discard changes?'}
			onConfirm={confirmCancel}
			onCancel={() => setShowConfirm(false)}
		/>
	);
}
